<!-- eslint-disable max-len -->
<template>
  <v-container>
    <ValidationObserver ref="form">
      <FormToolbar :title="edit ? 'Edit Vessel Activity' : 'New Vessel Activity'"
                   :onSubmitClick="submitForm"
                   :onCloseClick="onCloseClick"
                   :isSubmitting="isSubmitting" />
      <v-form class="mt-1">
        <v-container>
          <v-row>
            <v-col cols="8" md="4" class="py-0 ma-0">
              <v-dialog ref="startDateMenu"
                        v-model="startDateMenu"
                        transition="scale-transition"
                        width="unset">
                <template v-slot:activator="{ on }">
                  <ValidationProvider name="Start Date"
                                      rules="required"
                                      v-slot="{ errors }">
                    <v-text-field v-model="vesselActivity.startDate"
                                  label="Start Date"
                                  :error-messages="errors"
                                  readonly
                                  v-on="on"></v-text-field>
                  </ValidationProvider>
                </template>
                <v-date-picker v-model="vesselActivity.startDate"
                               @change="startDateSelected()"
                               :max="editingVesselConsumption?currentDate:null"
                               no-title
                               scrollable>
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="startDateMenu = false">Cancel</v-btn>
                </v-date-picker>
              </v-dialog>
            </v-col>
            <v-col cols="4" md="2" class="py-0 ma-0">
              <v-dialog ref="startTimeMenu"
                        v-model="startTimeMenu"
                        transition="scale-transition"
                        max-width="300">
                <template v-slot:activator="{ on }">
                  <v-text-field v-model="vesselActivity.startTime"
                                label="Start Time"
                                readonly
                                v-on="on"></v-text-field>
                </template>
                <v-time-picker v-if="startTimeMenu"
                               v-model="vesselActivity.startTime"
                               format="24hr"
                               :max="editingVesselConsumption?currentTime:null"
                               full-width
                               @change="startTimeSelected()"
                               @click:minute="startTimeMenu = false"></v-time-picker>
              </v-dialog>
            </v-col>
            <v-col cols="8" md="4" class="py-0 ma-0">
              <v-text-field v-if="editingVesselConsumption"
                            v-model="vesselActivity.endDate"
                            label="End Date"
                            disabled
                            v-on="on"></v-text-field>

              <v-dialog v-else
                        ref="endDateMenu"
                        v-model="endDateMenu"
                        transition="scale-transition"
                        width="unset">
                <template v-slot:activator="{ on }">
                  <v-text-field v-model="vesselActivity.endDate"
                                label="End Date"
                                readonly
                                v-on="on"></v-text-field>
                </template>
                <v-date-picker v-model="vesselActivity.endDate"
                               :min="vesselActivity.startDate"
                               @change="endDateSelected()"
                               no-title
                               scrollable>
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="endDateMenu = false">Cancel</v-btn>
                </v-date-picker>
              </v-dialog>
            </v-col>
            <v-col cols="4" md="2" class="py-0 ma-0">
              <v-text-field v-if="editingVesselConsumption"
                            v-model="vesselActivity.endTime"
                            label="End Time"
                            disabled
                            v-on="on"></v-text-field>

              <v-dialog v-else
                        ef="endTimeMenu"
                        v-model="endTimeMenu"
                        transition="scale-transition"
                        max-width="300px">
                <template v-slot:activator="{ on }">
                  <v-text-field v-model="vesselActivity.endTime"
                                label="End Time"
                                readonly
                                v-on="on"></v-text-field>
                </template>
                <v-time-picker v-if="endTimeMenu"
                               v-model="vesselActivity.endTime"
                               :min="getMinimumEndTime(vesselActivity)"
                               format="24hr"
                               full-width
                               @click:minute="endTimeMenu = false"></v-time-picker>
              </v-dialog>
            </v-col>
          </v-row>

          <!-- Hide when editing base activity -->
          <div v-if="!editingBaseActivity">
            <v-row>
              <v-col cols="12" md="6">
                <ValidationProvider name="Destination"
                                    rules="required"
                                    v-slot="{ errors }">
                  <v-autocomplete v-model="vesselActivity.location"
                                  :items="locations"
                                  item-text="name"
                                  item-value="id"
                                  return-object
                                  :error-messages="errors"
                                  label="Destination"
                                  @change="activityLocationChanged()"></v-autocomplete>
                </ValidationProvider>
              </v-col>
              <v-col cols="12" md="3">
                <ValidationProvider name="Distance"
                                    :rules="distanceRequired ? 'required' : ''"
                                    v-slot="{ errors }">
                  <v-text-field type="number"
                                v-model="vesselActivity.distanceSailed"
                                :error-messages="errors"
                                label="Distance Sailed"
                                :disabled="!distanceRequired" />
                </ValidationProvider>
              </v-col>
              <v-col cols="12" md="3">
                <ValidationProvider name="Deckspace Used"
                                    rules="required|between:0,100"
                                    v-slot="{ errors }">
                  <v-text-field type="number"
                                v-model="vesselActivity.deckSpaceUsed"
                                :error-messages="errors"
                                label="Deckspace Used"/>
                </ValidationProvider>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="4">
                <ValidationProvider name="Activity Name"
                                    rules="required"
                                    v-slot="{ errors }">
                  <v-autocomplete v-model="vesselActivity.activityName"
                                  :items="activitityNamesForDropdown"
                                  item-text="activityName"
                                  item-value="activityName"
                                  :error-messages="errors"
                                  label="Activity"
                                  @change="activityNameChanged()"
                                  :disabled="!activityNameActive"></v-autocomplete>
                </ValidationProvider>
              </v-col>
              <v-col cols="12" md="4">
                <ValidationProvider name="Activity Type"
                                    rules="required"
                                    v-slot="{ errors }">
                  <v-autocomplete v-model="vesselActivity.activityId"
                                  :items="activityTypesForDropdown"
                                  item-text="activityType"
                                  item-value="id"
                                  :error-messages="errors"
                                  label="Activity Type"
                                  @change="activityTypeChanged()"
                                  :disabled="!activityTypeActive"></v-autocomplete>
                </ValidationProvider>
              </v-col>
              <v-col cols="12" md="4">
                <ValidationProvider v-slot="{ errors }">
                  <v-autocomplete v-model="vesselActivity.activityInfo"
                                  :items="activityInfosForDropdown"
                                  item-text="activityInfo"
                                  item-value="activityInfo"
                                  :error-messages="errors"
                                  label="Activity Info"
                                  :disabled="!activityInfoActive"></v-autocomplete>
                </ValidationProvider>
              </v-col>
            </v-row>
          </div>

          <v-row>
            <v-col cols="12">
              <ValidationProvider name="Vessel Comment"
                                  :rules="commentRequired ? 'max:1000|required' : 'max:1000'"
                                  v-slot="{ errors }">
                <v-textarea v-model="vesselActivity.vesselComment"
                            label="Vessel Comment"
                            :error-messages="errors"
                            :counter="1000"></v-textarea>
              </ValidationProvider>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <ValidationProvider name="Validation Comment"
                                  rules="max:1000"
                                  v-slot="{ errors }">
                <v-textarea v-model="vesselActivity.verificationComment"
                            label="Verification Comment"
                            :error-messages="errors"
                            :counter="1000"></v-textarea>
              </ValidationProvider>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </ValidationObserver>
  </v-container>
</template>

<script>
  import { ValidationProvider, ValidationObserver } from 'vee-validate';
  import { mapState, mapGetters } from 'vuex';
  import FormToolbar from '../ui/FormToolbar.vue';
  import '../../validators';
  import VesselActivityDeckSpaceCalculatorMixin from '@/mixins/VesselActivityDeckSpaceCalculatorMixin';
  import dateTimeHelpers from '../../mixins/DateTimeHelpers';

  export default {
    props: {
      isOpen: Boolean,
      vesselActivity: Object,
      previousActivity: Object,
      edit: Boolean,
      onSubmitClick: Function,
      onCloseClick: Function,
      isSubmitting: Boolean,
      bus: Object,
      vesselActivities: Array,
    },
    components: {
      ValidationProvider,
      ValidationObserver,
      FormToolbar,
    },
    mixins: [VesselActivityDeckSpaceCalculatorMixin],
    data() {
      return {
        startDateMenu: false,
        startTimeMenu: false,
        endDateMenu: false,
        endTimeMenu: false,

        activityNameActive: false,
        activityTypeActive: false,
        activityInfoActive: false,

        activitityNamesForDropdown: null,
        activityTypesForDropdown: null,
        activityInfosForDropdown: null,

        distanceRequired: false,
        commentRequired: false,

        offshoreTypeString: 'Offshore',
        portTypeString: 'Port',

        currentDate: dateTimeHelpers.getCurrentDateLt(),
        currentTime: dateTimeHelpers.getCurrentTimeLt()
      };
    },
    created() {
      this.bus.$on('resetForm', this.resetForm);
    },
    mounted() {
      if (this.edit) {
        this.configureFormForEdit();
      } else {
        // default to stationary activities
        this.activitityNamesForDropdown = this.stationaryActivities;
        this.activityTypesForDropdown = this.stationaryActivities;
        this.activityInfosForDropdown = this.stationaryActivities;
        this.resetLocationValue();
        this.dialogOpened();
      }
    },
    computed: {
      ...mapState(['activitiesStore', 'locationsStore', 'voyageReportsStore', 'userStore']),
      ...mapGetters({
        getActivityById: 'activitiesStore/getActivityById',
        getSyncedAndUnSyncedCargoForVoyage: 'voyageReportsStore/getSyncedAndUnSyncedCargoForVoyage',
      }),
      activities() {
        return this.activitiesStore ? this.activitiesStore.activities : null;
      },
      voyageReport() {
        return this.voyageReportsStore ? this.voyageReportsStore.voyageReportDetailsData : null;
      },
      locations() {
        return this.locationsStore ? this.locationsStore.locations : null;
      },
      stationaryActivities() {
        return this.activities
          .filter((x) => x.isSailingActivity === false && x.isShiftInPortActivity === false);
      },
      activeSiteECargoEnabled() {
        return this.userStore.user.activeSiteConfig.isECargoEnabled;
      },
      movementActivities() {
        return this.activities
          .filter((x) => x.isSailingActivity || x.isShiftInPortActivity);
      },
      editingBaseActivity() {
        return this.edit && !(this.vesselActivity.activityId || this.vesselActivity.activity);
      },
      outboundVoyageCargo() {
        return this.getSyncedAndUnSyncedCargoForVoyage(true);
      },
      inboundVoyageCargo() {
        return this.getSyncedAndUnSyncedCargoForVoyage(false);
      },
      editingVesselConsumption() {
        return this.vesselActivity.activityType && this.vesselActivity.activityType.match(/Vessel Consumption/i);
      },
    },
    watch: {
      isOpen(val) {
        if (this.edit) {
          this.configureFormForEdit();
        }
      },
      previousActivity(newVal) {
        if (!this.edit) {
          this.vesselActivity.location = newVal.location;
        }
      },
    },
    methods: {
      dialogOpened() {
        if (this.activeSiteECargoEnabled) {
          this.vesselActivity.deckSpaceUsed = this.getDeckSpaceForActivity(
            this.vesselActivities,
            this.voyageReportsStore.eCargoVoyageCargo.outbound,
            this.voyageReportsStore.eCargoVoyageCargo.inbound,
            this.outboundVoyageCargo,
            this.inboundVoyageCargo
          );         
        }
      },
      submitForm() {
        this.$refs.form.validate()
          .then((isValid) => {
            if (isValid) this.onSubmitClick();
          });
      },
      resetForm() {
        this.$refs.form.reset();
        this.$nextTick(() => {
          this.resetLocationValue();
          this.distanceRequired = false;
          this.commentRequired = false;
          this.dialogOpened();
        });
      },
      resetActivityDropdownValues() {
        // default to stationary activities (Location not yet been changed)
        this.activitityNamesForDropdown = this.stationaryActivities;
        this.activityTypesForDropdown = this.stationaryActivities;
        this.activityInfosForDropdown = this.stationaryActivities;

        this.activityNameActive = true;
        this.activityTypeActive = false;
        this.activityInfoActive = false;
      },
      activityLocationChanged() {
        if (!this.editingBaseActivity) {
          this.resetActivityDropdownValues();

          this.activitityNamesForDropdown = this.activitityNamesForDropdown
            .filter((activity) => activity.locationType === this.vesselActivity.location.type);

          // set previous location to previous activity location or the voyage initial port
          // if there is no previous activity
          const prevLocation = this.previousActivity
            ? this.previousActivity.location : this.voyageReport.initialPortLocation;

          if (prevLocation) {
            // if location has changed from previous activity - filter and autocomplete
            if (this.vesselActivity.location.id !== prevLocation.id) {
              // Filter to movement activities+
              this.activitityNamesForDropdown = this.movementActivities;
              this.activityNameActive = true;

              const newLocation = this.vesselActivity.location;
              const newLocationTypeString = newLocation.type;
              const prevLocationTypeString = prevLocation.type;

              if (newLocation
                && (newLocationTypeString === this.portTypeString
                  && prevLocationTypeString === this.portTypeString)) {
                // Filter activity drop downs to 'SHIFT' activity and autocomplete
                this.autocompleteShifting();
              } else {
                // filter activitiy drop downs to Sailing Time activities and autocomplete
                this.autocompleteSailingTime();
              }
            } else {
              // reset filter stationary activities by location type
              this.activitityNamesForDropdown = this.stationaryActivities
                .filter((activity) => activity.locationType === this.vesselActivity.location.type);
              this.activityNameActive = true;
            }
          }
        }
      },
      activityNameChanged() {
        this.vesselActivity.activityType = '';
        this.vesselActivity.activityInfo = '';

        this.activityTypesForDropdown = this.activitityNamesForDropdown
          .filter((activity) => activity.activityName === this.vesselActivity.activityName);
        this.activityTypeActive = true;
      },
      activityTypeChanged() {
        this.vesselActivity.activityInfo = '';

        const theActivity = this.getActivityById(this.vesselActivity.activityId);
        if (theActivity) {
          this.vesselActivity.activity = { ...theActivity };
          this.vesselActivity.code = theActivity.code ?? null;
          this.vesselActivity.activityType = theActivity.activityType;
          this.activityInfosForDropdown = this.activityTypesForDropdown
            .filter((activity) => activity.activityType === this.vesselActivity.activityType);

          this.commentRequired = theActivity.isCommentRequired; // set required comment flag
          this.distanceRequired = theActivity.isSailingActivity || theActivity.isShiftInPortActivity;

          this.activityInfoActive = true;
        }
      },
      startDateSelected() {
        this.startDateMenu = false;
        this.startTimeMenu = true;
        
        if (this.editingVesselConsumption)
          this.vesselActivity.endDate = this.vesselActivity.startDate;
      },
      startTimeSelected() {
        if (this.editingVesselConsumption)
          this.vesselActivity.endTime = this.vesselActivity.startTime;
      },
      endDateSelected() {
        this.endDateMenu = false;
        this.endTimeMenu = true;
      },
      getMinimumEndTime(activity) {
        if (activity.startDate === activity.endDate) {
          return activity.startTime;
        }
        return null;
      },
      autocompleteSailingTime() {
        this.activitityNamesForDropdown = this.movementActivities
          .filter((x) => x.isSailingActivity === true);

        this.vesselActivity.activityName = this.activitityNamesForDropdown[0].activityName;
        this.activityNameChanged();

        this.vesselActivity.activityId = this.activityTypesForDropdown[0].id;
        this.activityTypeChanged();
      },
      autocompleteShifting() {
        this.activitityNamesForDropdown = this.movementActivities
          .filter((x) => x.isShiftInPortActivity === true);

        this.vesselActivity.activityName = this.activitityNamesForDropdown[0].activityName;
        this.activityNameChanged();

        this.vesselActivity.activityId = this.activityTypesForDropdown[0].id;
        this.activityTypeChanged();
      },
      resetLocationValue() {
        if (this.previousActivity) {
          this.vesselActivity.location = this.previousActivity.location;
        } else {
          this.vesselActivity.location = this.voyageReport.initialPortLocation;
        }

        if (this.vesselActivity.location) this.activityLocationChanged();
      },
      setActivitiesToStationaryByLocationType(locationType) {
        this.activitityNamesForDropdown = this.stationaryActivities
          .filter((x) => x.locationType === locationType);
        this.activityTypesForDropdown = this.stationaryActivities
          .filter((x) => x.locationType === locationType);
        this.activityInfosForDropdown = this.stationaryActivities
          .filter((x) => x.locationType === locationType);
      },
      configureFormForEdit() {
        this.activityNameActive = true;
        this.activityTypeActive = true;
        this.activityInfoActive = true;

        this.activitityNamesForDropdown = this.activities;
        this.activityTypesForDropdown = this.activities;
        this.activityInfosForDropdown = this.activities;

        if (this.vesselActivity.activity) {
          if (this.vesselActivity.activity.isSailingActivity === true) {
            this.autocompleteSailingTime();
          } else if (this.vesselActivity.activity.isShiftInPortActivity === true) {
            this.autocompleteShifting();
          } else {
            this.setActivitiesToStationaryByLocationType(this.vesselActivity.activity.locationType);
          }
        }
      },
    },
  };
</script>

<style>
</style>
